import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Ban, RefreshCw, Smartphone, Wrench, WifiOff } from 'lucide-react';
import { I18nProvider, guessLang, useI18n } from '@/i18n';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { getTgUser } from '@/lib/telegram';
import { Layout } from '@/components/Layout';
import { Screen, BrandMark, Spinner } from '@/components/ui';
import { HomePage } from '@/pages/HomePage';
import { RegisterPage } from '@/pages/RegisterPage';
import { MethodsPage } from '@/pages/MethodsPage';
import { DepositMethodPage } from '@/pages/DepositMethodPage';
import { WithdrawMethodPage } from '@/pages/WithdrawMethodPage';
import { TxPage } from '@/pages/TxPage';
import { HistoryPage } from '@/pages/HistoryPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ReferralsPage } from '@/pages/ReferralsPage';
import { GiftCodesPage } from '@/pages/GiftCodesPage';
import { SupportPage } from '@/pages/SupportPage';

const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } }
});

const router = createBrowserRouter([
    {
        path: '/',
        element: <Layout />,
        children: [
            { index: true, element: <HomePage /> },
            { path: 'register', element: <RegisterPage /> },
            { path: 'deposit', element: <MethodsPage direction="deposit" /> },
            { path: 'deposit/:key', element: <DepositMethodPage /> },
            { path: 'withdraw', element: <MethodsPage direction="withdraw" /> },
            { path: 'withdraw/:key', element: <WithdrawMethodPage /> },
            { path: 'tx/:id', element: <TxPage /> },
            { path: 'history', element: <HistoryPage /> },
            { path: 'profile', element: <ProfilePage /> },
            { path: 'referrals', element: <ReferralsPage /> },
            { path: 'gift-codes', element: <GiftCodesPage /> },
            { path: 'support', element: <SupportPage /> },
            { path: '*', element: <Navigate to="/" replace /> }
        ]
    }
], { basename: '/app' });

function Gate() {
    const { t } = useI18n();
    const { loading, me, error, refresh } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <BrandMark size="xl" glow />
                <Spinner className="w-5 h-5 text-brand-300" />
            </div>
        );
    }
    if (error || !me) {
        const code = error?.error;
        if (code === 'banned') return <Screen icon={<Ban className="w-10 h-10 text-red-300" />} title={t('screens.banned.title')} text={t('screens.banned.text')} />;
        if (code === 'maintenance') {
            return (
                <Screen icon={<Wrench className="w-10 h-10" />} title={t('screens.maintenance.title')} text={t('screens.maintenance.text')}>
                    <button className="btn-secondary" onClick={() => void refresh()}><RefreshCw className="w-4 h-4" /> {t('common.tryAgain')}</button>
                </Screen>
            );
        }
        if (code === 'unauthorized' || code === 'session_expired') {
            return <Screen icon={<Smartphone className="w-10 h-10" />} title={t(`screens.${code === 'session_expired' ? 'expired' : 'openInTelegram'}.title`)} text={t(`screens.${code === 'session_expired' ? 'expired' : 'openInTelegram'}.text`)} />;
        }
        return (
            <Screen icon={<WifiOff className="w-10 h-10" />} title={t('screens.offline.title')} text={t('screens.offline.text')}>
                <button className="btn-primary" onClick={() => void refresh()}><RefreshCw className="w-4 h-4" /> {t('common.tryAgain')}</button>
            </Screen>
        );
    }
    return <RouterProvider router={router} />;
}

export default function App() {
    return (
        <I18nProvider initial={guessLang(getTgUser()?.language_code || navigator.language)}>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <Gate />
                </AuthProvider>
                <Toaster position="top-center" toastOptions={{
                    duration: 2200,
                    style: { background: '#0e150f', color: '#f2f7ee', border: '1px solid rgba(158,240,31,.35)', fontSize: '13px' },
                    success: { iconTheme: { primary: '#9ef01f', secondary: '#060a06' } }
                }} />
            </QueryClientProvider>
        </I18nProvider>
    );
}
